import { memo } from 'react';

import cx from 'classnames';
import { useSelector } from 'react-redux';

import { MaybeVirtualizer } from 'components/shared/LandingPage/Virtualizer';
import { useCustomFonts, useCustomStyles } from 'hooks/useCustomFront';
import { useWebViewLinksHandler } from 'hooks/useWebViewLinksHandler';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { IProposal } from 'utils/itineraryDetails/types';

import { LANDING_PAGES_CONTAINER_ID } from '../../components/shared/LandingPage/components/Grid';
import { LandingV2ContextProvider, useLandingV2Context } from './context';
import { useContentProps, useLandingV2ContextValue, useManualScrollWatcher } from './functions';
import Header from './Header';
import s from './index.module.css';
import Page from './Page';
import { IPageProps } from './Page/types';
import { ILandingV2Props } from './types';
import { getPrefix } from './utils';

const LandingV2 = (props: ILandingV2Props) => {
  const contextValue = useLandingV2ContextValue(props);

  return (
    <LandingV2ContextProvider value={contextValue}>
      <LandingV2ContextConsumer />
    </LandingV2ContextProvider>
  );
};

export default memo(LandingV2);

const LandingV2ContextConsumer = () => {
  useManualScrollWatcher();
  useWebViewLinksHandler();

  const {
    config,
    data,
    contentRef,
    landingRef,
    readonly,
    deviceType,
    pageHandlersProvider: PageHandlersProvider,
  } = useLandingV2Context();

  useCustomStyles({ css: config.css, customId: data.id });
  useCustomFonts({ landingItem: data, customId: data.id });

  const contentProps = useContentProps();

  const landing = useSelector(itinerariesItemDataSelector) as IProposal;

  return (
    <div
      ref={landingRef}
      id={LANDING_PAGES_CONTAINER_ID}
      className={cx(getPrefix('container'), s.container, deviceType)}
    >
      {config.header !== false ? <Header config={config.header} /> : null}
      <div className={cx(getPrefix('content'), s.content)} ref={contentRef} {...contentProps}>
        <MaybeVirtualizer
          getEnabled={() =>
            deviceType === 'desktop' &&
            !readonly &&
            (landing.landingConfig?.pages?.every((p) => p.layout === 'halfWidth') ?? true)
          }
          count={data.landing.pages.length}
          getContentRef={() => contentRef}
          getRerenderDeps={({ visibleItems }) => [!visibleItems || visibleItems.length === 0 ? null : landing]}
        >
          {({ getPageVirtualized }: { getPageVirtualized?: (i: number) => IPageProps['virtualized'] } = {}) => (
            <>
              {data.landing.pages.map((p, i, arr) => (
                <PageHandlersProvider key={p.id} pageIndex={i}>
                  {({ changeCoverImage, changeContent }) => (
                    <Page
                      index={i}
                      data={p}
                      onChange={changeContent}
                      onChangeCoverImage={changeCoverImage}
                      landingConfig={config}
                      readonly={readonly}
                      isFirst={i === 0}
                      isLast={i === arr.length - 1}
                      virtualized={getPageVirtualized?.(i)}
                    />
                  )}
                </PageHandlersProvider>
              ))}
            </>
          )}
        </MaybeVirtualizer>
      </div>
    </div>
  );
};
